import React from 'react';
import { ChevronRight, AlertCircle } from 'lucide-react';
import { SUR_TITRE } from './DashboardUi';

/**
 * Les blocs du tableau de bord Restauration (maquette « Tableau de bord Restauration », 17a).
 *
 * Chaque bloc se rend vide-safe : appelé sans donnée, il ne s'affiche pas du tout plutôt que de
 * montrer un cadre avec un zéro dedans. C'est la règle de l'écran — il ne dit que ce qu'il sait.
 */

export type Tone = 'succes' | 'attention' | 'danger' | 'info';

/** Les quatre couleurs d'état de la charte, jamais une teinte inventée par bloc. */
export const TONE_CLASSES: Record<Tone, string> = {
    succes: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
    attention: 'bg-state-warn-soft border-state-warn/30 text-state-warn',
    danger: 'bg-state-danger-soft border-state-danger/30 text-state-danger',
    info: 'bg-state-info-soft border-state-info/30 text-state-info',
};

export const Carte: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div className={`p-4 rounded-xl bg-surface border border-border ${className}`}>{children}</div>
);

export const EnTeteBloc: React.FC<{ titre: string; droite?: React.ReactNode }> = ({ titre, droite }) => (
    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <span className={SUR_TITRE}>{titre}</span>
        {droite}
    </div>
);

export interface Todo {
    key: string; count: number; label: string; detail: string; verb: string; tone: Tone; href: string;
}

/** « À traiter aujourd'hui » : ce qui attend une décision, et rien d'autre. */
export const BlocATraiter: React.FC<{ todos: Todo[]; onOuvrir: (href: string) => void }> = ({ todos, onOuvrir }) => {
    if (!todos.length) return null;
    return (
        <section className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-x-3.5 gap-y-1 flex-wrap">
                <span className={SUR_TITRE}>À traiter aujourd'hui</span>
                <span className="text-[11.5px] leading-snug text-content-muted">
                    Chaque ligne disparaît d'elle-même une fois l'action faite.
                </span>
            </div>
            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(255px,1fr))]">
                {todos.map((t) => (
                    <button key={t.key} type="button" onClick={() => onOuvrir(t.href)}
                        className="p-3.5 rounded-xl bg-surface border border-border flex flex-col gap-2.5 text-left hover:border-content transition-colors">
                        <div className="flex items-center gap-2.5">
                            <span className={`inline-flex items-center justify-center min-w-[30px] h-[26px] px-2 rounded-lg border text-sm font-bold tabular-nums shrink-0 ${TONE_CLASSES[t.tone]}`}>
                                {t.count}
                            </span>
                            <span className="text-[13px] font-bold tracking-tight text-content min-w-0">{t.label}</span>
                        </div>
                        <p className="text-[11.5px] leading-snug text-content-muted text-pretty">{t.detail}</p>
                        <span className="inline-flex items-center gap-1.5 self-start h-[30px] px-3 rounded-full border border-border bg-surface-soft text-xs font-semibold text-content whitespace-nowrap">
                            {t.verb}<ChevronRight size={13} />
                        </span>
                    </button>
                ))}
            </div>
        </section>
    );
};

export interface Service { name: string; staff: number; state: string; tone: Tone }

/** « La journée » : qui est posté au midi et au soir. Pas de couverts — l'app ne les connaît pas. */
export const BlocJournee: React.FC<{ services: Service[]; absences: { nom: string; type: string }[] }> = ({ services, absences }) => (
    <Carte>
        <EnTeteBloc
            titre="La journée"
            droite={<span className="text-[11.5px] text-content-muted first-letter:uppercase">
                {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>}
        />
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]">
            {services.map((s) => (
                <div key={s.name} className="px-3.5 py-3 rounded-[10px] bg-surface-soft border border-border-soft">
                    <div className="flex items-center justify-between gap-2.5">
                        <span className="text-[13px] font-bold text-content">{s.name}</span>
                        <span className={`inline-flex items-center h-[22px] px-2.5 rounded-md border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shrink-0 ${TONE_CLASSES[s.tone]}`}>
                            {s.state}
                        </span>
                    </div>
                    <div className="mt-3 pt-2.5 border-t border-border-soft">
                        <div className="text-xl font-bold tracking-tight tabular-nums leading-none text-content">{s.staff}</div>
                        <div className="mt-1 text-[9.5px] uppercase tracking-wider text-content-muted">Salariés postés</div>
                    </div>
                </div>
            ))}
        </div>
        {absences.length > 0 && (
            <p className="mt-3 text-[11.5px] leading-snug text-content-muted text-pretty">
                {absences.length === 1 ? 'Absent aujourd\'hui : ' : 'Absents aujourd\'hui : '}
                {absences.map((a) => `${a.nom} (${a.type})`).join(', ')}.
            </p>
        )}
    </Carte>
);

export interface Ligne {
    id: string; name: string; when?: string | null; detail?: string; step?: string; tone: Tone;
}

/** Liste de dossiers ou d'événements : un filet coloré, un nom, un état. */
export const BlocListe: React.FC<{
    titre: string; lien?: string; lienLabel?: string; lignes: Ligne[];
    onOuvrir: (id: string) => void; vide: string;
}> = ({ titre, lienLabel, lien, lignes, onOuvrir, vide }) => (
    <Carte>
        <EnTeteBloc
            titre={titre}
            droite={lien && lienLabel ? (
                <a href={lien} className="text-xs font-semibold text-content hover:underline">{lienLabel}</a>
            ) : undefined}
        />
        {lignes.length === 0 ? (
            <p className="text-[11.5px] text-content-muted">{vide}</p>
        ) : (
            <div className="flex flex-col">
                {lignes.map((l, i) => (
                    <button key={l.id} type="button" onClick={() => onOuvrir(l.id)}
                        className={`flex items-center gap-3 px-0.5 py-2.5 text-left hover:bg-surface-soft transition-colors ${i === 0 ? '' : 'border-t border-border-soft'}`}>
                        <span className={`w-1 h-[34px] shrink-0 rounded-full ${TONE_CLASSES[l.tone].split(' ').find((c) => c.startsWith('text-'))?.replace('text-', 'bg-')}`} />
                        <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-bold tracking-tight text-content truncate">{l.name}</span>
                            <span className="block mt-0.5 text-[11.5px] text-content-muted truncate">
                                {[l.when, l.detail].filter(Boolean).join(' · ')}
                            </span>
                        </span>
                        {l.step && (
                            <span className={`inline-flex items-center h-6 px-2.5 rounded-full border text-[11px] font-bold whitespace-nowrap shrink-0 ${TONE_CLASSES[l.tone]}`}>
                                {l.step}
                            </span>
                        )}
                        <ChevronRight size={15} className="text-content-muted shrink-0" />
                    </button>
                ))}
            </div>
        )}
    </Carte>
);

/** « Effectif posté, jour par jour » : sept colonnes, le jour courant en relief. */
export const BlocSemaine: React.FC<{
    jours: { date: string; label: string; midi: number; soir: number; aujourdhui: boolean }[];
    onOuvrirPlanning: () => void;
}> = ({ jours, onOuvrirPlanning }) => {
    if (!jours.length) return null;
    return (
        <Carte>
            <EnTeteBloc
                titre="Effectif posté, jour par jour"
                droite={<button type="button" onClick={onOuvrirPlanning} className="text-xs font-semibold text-content hover:underline">Ouvrir le planning</button>}
            />
            <div className="grid grid-cols-7 gap-2">
                {jours.map((j) => (
                    <div key={j.date} className={`px-2.5 py-2.5 rounded-[10px] border ${
                        j.aujourdhui ? 'bg-surface border-content' : 'bg-surface-soft border-border-soft'
                    }`}>
                        <div className={`text-[10.5px] font-bold uppercase tracking-wider whitespace-nowrap ${j.aujourdhui ? 'text-content' : 'text-content-muted'}`}>
                            {j.label}
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-2">
                            <span className="text-[15px] font-bold tabular-nums leading-none text-content">{j.midi}</span>
                            <span className="text-[9.5px] uppercase tracking-wider text-content-muted">midi</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-1">
                            <span className="text-[15px] font-bold tabular-nums leading-none text-content">{j.soir}</span>
                            <span className="text-[9.5px] uppercase tracking-wider text-content-muted">soir</span>
                        </div>
                    </div>
                ))}
            </div>
        </Carte>
    );
};

/** Le coût de la semaine, et pourquoi il est parfois incalculable. */
export const BlocCoutSemaine: React.FC<{
    semaine: {
        week_start: string; publiee: boolean; creneaux: number; salaries: number; cout: number | null;
        /** Créneaux dont le salarié n'a pas de taux : le total les ignore, il faut donc le dire. */
        creneaux_sans_taux?: number;
    };
    onEtape?: (href: string) => void;
}> = ({ semaine, onEtape }) => (
    <Carte>
        <div className="flex items-center justify-between gap-2.5 mb-3">
            <span className={SUR_TITRE}>
                Semaine du {new Date(`${semaine.week_start}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
            </span>
            <span className={`inline-flex items-center h-[22px] px-2.5 rounded-md border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shrink-0 ${
                semaine.publiee ? TONE_CLASSES.succes : TONE_CLASSES.attention
            }`}>
                {semaine.publiee ? 'Publiée' : 'Non publiée'}
            </span>
        </div>
        <div className="flex items-baseline gap-1.5">
            <span className="text-[26px] font-bold tracking-tight tabular-nums leading-none text-content">{semaine.creneaux}</span>
            <span className="text-xs text-content-muted">créneaux · {semaine.salaries} salarié{semaine.salaries > 1 ? 's' : ''}</span>
        </div>
        <div className="mt-3 pt-3 border-t border-border-soft">
            <div className="text-[12.5px] font-bold text-content">Coût de la semaine</div>
            {semaine.cout === null ? (
                <>
                    <p className="mt-0.5 text-[11.5px] text-content-muted">Aucun taux horaire renseigné — le coût ne peut pas être calculé.</p>
                    {onEtape && (
                        <button type="button" onClick={() => onEtape('/employes')}
                            className="mt-2 h-8 inline-flex items-center px-3 rounded-full border border-border bg-surface text-[12px] font-semibold text-content hover:border-content transition-colors">
                            Renseigner les taux
                        </button>
                    )}
                </>
            ) : (
                <>
                    <p className="mt-0.5 text-[20px] font-bold tabular-nums text-content leading-none">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(semaine.cout)}
                    </p>
                    {/* UN CHIFFRE PARTIEL EST UN CHIFFRE FAUX tant qu'on ne dit pas qu'il est partiel : sans
                        cette ligne, un établissement dont la moitié des salariés n'ont pas de taux
                        planifierait contre un coût sous-évalué sans jamais s'en apercevoir. */}
                    {(semaine.creneaux_sans_taux || 0) > 0 && (
                        <p className="mt-1 text-[11.5px] text-state-warn">
                            {semaine.creneaux_sans_taux} créneau{(semaine.creneaux_sans_taux || 0) > 1 ? 'x' : ''} non compté{(semaine.creneaux_sans_taux || 0) > 1 ? 's' : ''} — taux horaire manquant.
                            {onEtape && (
                                <button type="button" onClick={() => onEtape('/employes')} className="ml-1.5 underline font-semibold">
                                    Les renseigner
                                </button>
                            )}
                        </p>
                    )}
                </>
            )}
        </div>
    </Carte>
);

/** Écran du premier jour (17b) : rien à traiter, donc on dit quoi faire. */
export const BlocPremierJour: React.FC<{ onEtape: (href: string) => void }> = ({ onEtape }) => {
    const etapes = [
        { n: '1', title: 'Ajouter vos salariés et leurs postes', detail: "C'est ce qui remplit la grille du planning et les feuilles de route.", href: '/postes-employes' },
        { n: '2', title: 'Publier le planning de la semaine', detail: 'Chaque salarié reçoit ses créneaux sur son portail.', href: '/planning' },
        { n: '3', title: 'Renseigner les taux horaires', detail: 'Le coût de la semaine se calcule ensuite tout seul.', href: '/postes-employes' },
    ];
    return (
        <div className="flex flex-col gap-2.5">
            <p className="text-[13px] text-content-muted">Bienvenue. Trois choses à poser avant le premier service, dans cet ordre.</p>
            {etapes.map((e) => (
                <button key={e.n} type="button" onClick={() => onEtape(e.href)}
                    className="flex items-center gap-3 p-3.5 rounded-xl bg-surface border border-border text-left hover:border-content transition-colors">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-surface-soft border border-border text-content flex items-center justify-center text-[12.5px] font-bold tabular-nums">
                        {e.n}
                    </span>
                    <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-bold text-content">{e.title}</span>
                        <span className="block mt-0.5 text-[11.5px] leading-snug text-content-muted text-pretty">{e.detail}</span>
                    </span>
                    <ChevronRight size={15} className="text-content-muted shrink-0" />
                </button>
            ))}
            <div className="p-3.5 rounded-xl bg-surface border border-dashed border-border text-center">
                <p className="text-[12.5px] text-content-muted">
                    Les chiffres et les alertes apparaîtront ici dès le premier planning publié.
                </p>
            </div>
        </div>
    );
};

/** Bloc « Alertes » — seulement ce qui est réellement détecté, jamais un cadre vide. */
export const BlocAlertes: React.FC<{ alertes: { title: string; detail: string; tone: Tone }[] }> = ({ alertes }) => {
    if (!alertes.length) return null;
    return (
        <Carte>
            <div className={`${SUR_TITRE} mb-3`}>Alertes</div>
            <div className="flex flex-col gap-2.5">
                {alertes.map((a, i) => (
                    <div key={i} className="flex gap-2.5">
                        <span className={`w-[26px] h-[26px] shrink-0 rounded-[7px] border flex items-center justify-center ${TONE_CLASSES[a.tone]}`}>
                            <AlertCircle size={13} />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[12.5px] font-bold text-content">{a.title}</div>
                            <p className="mt-0.5 text-[11.5px] leading-snug text-content-muted text-pretty">{a.detail}</p>
                        </div>
                    </div>
                ))}
            </div>
        </Carte>
    );
};

/** Support — le seul bloc purement statique de l'écran. */
export const BlocSupport: React.FC = () => (
    <Carte>
        <div className={`${SUR_TITRE} mb-2.5`}>Support L'IAmani</div>
        <p className="text-[12.5px] leading-relaxed text-content-muted text-pretty">
            Une question, une modification, un besoin qui vous faciliterait le travail : écrivez-nous depuis
            la barre latérale.
        </p>
    </Carte>
);
